// 本地存档（spec §8）。网页用 localStorage；App（P5）换成 Capacitor Preferences —— 只需实现 KeyValueStore。
import type { Board, Par } from '../puzzle';
import { imageAspect, type Manifest } from '../content/manifest';
import { dialOf, puzzlesOf } from '../content/puzzles';

import { dailyGrid, dailySize, gridKey, levelGrid, MAX_SIDE, sameGrid, type Grid } from './levels';

/** 成绩按格数记（方形 '4'..'9'，长方形 '4x6'…'7x10'，见 levels.gridKey）。每个地点的格数由关卡表决定（core/levels），玩家不选难度 */
export type Best = { bestTimeMs: number; bestMoves: number };

export type PlaceSave = {
  unlocked: boolean;
  completed: boolean;
  catFound: boolean;
  /** 按格数记的最佳成绩（用时、步数各自独立取最好值）；关卡表改过格数时旧成绩留着不显示 */
  best: Record<string, Best>;
  /** 最好的星级（1..3）；缺省 = 还没有（旧存档没有这个字段） */
  stars?: number;
  /** 符文（月亮塔罗）是否找到；缺省 = 没有 */
  runeFound?: boolean;
};

/** 一个星盘锁（月亮塔罗每章一个）的进度；全部字段可选（旧存档没有 dials） */
export type DialSave = {
  solved?: boolean;
  /** 答错次数（≥3 出现"提示"） */
  wrong?: number;
  /** 已用提示揭开的格数（从第一格起按答案顺序） */
  hints?: number;
  /** 正在摆的格子（地点 id = 那张牌的符文；null = 空），重开 App 后接着摆 */
  slots?: (string | null)[];
};

export type PlayMode = 'journey' | 'daily';

export type InProgress = {
  mode: PlayMode;
  themeId: string;
  placeId: string;
  /** 每日挑战：这一局属于哪一天（YYYY-MM-DD）；过了这天就作废 */
  date?: string;
  /** 列数（方形 = 边长） */
  n: number;
  /** 行数；缺省 = n（方形，旧存档都是方形） */
  rows?: number;
  seed: number;
  board: Board;
  moves: number;
  elapsedMs: number;
  /** 本局开局时算出的标准步数（星级用，见 puzzle/stars）；旧存档缺省时按 seed 重新算 */
  par?: Par;
};

export type ThemeSave = {
  places: Record<string, PlaceSave>;
  endingSeen: boolean;
  /** 星盘锁，键 = 章节下标（'0' '1' '2'） */
  dials?: Record<string, DialSave>;
};
/** n = 当天的格数（按星期循环）；抽到竖版图时 n×rows 是实际的格（见 levels.dailyGrid） */
export type DailyResult = Best & { themeId: string; placeId: string; n?: number; rows?: number; stars?: number };
export type DailySave = {
  /** 每天第一次完成的成绩（之后重玩不覆盖） */
  results: Record<string, DailyResult>;
  streak: number;
  bestStreak: number;
  lastDate: string | null;
};
export type Settings = { haptics: boolean; gyro: boolean };
export type Stats = { puzzlesCompleted: number; totalPlayMs: number };

/** 存档 v2（v2 spec §5） */
export type Save = {
  version: 2;
  themes: Record<string, ThemeSave>;
  inProgress?: InProgress;
  daily: DailySave;
  settings: Settings;
  stats: Stats;
  lastThemeId: string | null;
  onboarding: { step: number };
  /** 新玩法第一次出现时的一步讲解是否看过（缺省 = 都没看过） */
  intros?: { rotate?: boolean; flip?: boolean };
  gyro: { asked: boolean; granted: boolean | null };
};

/** 键值后端：localStorage 是同步的，Capacitor Preferences 是异步的，接口按异步定义 */
export interface KeyValueStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export class LocalStorageStore implements KeyValueStore {
  constructor(private readonly prefix = 'paper-town:') {}
  async get(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(this.prefix + key);
    } catch {
      return null;
    }
  }
  async set(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(this.prefix + key, value);
    } catch (err) {
      console.warn('save failed', err); // 配额满 / 隐私模式：不让游戏崩
    }
  }
  async remove(key: string): Promise<void> {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch {
      /* ignore */
    }
  }
}

export const SAVE_KEY = 'save';
export const BACKUP_KEY = 'save.bak';
/** 开局解锁的地点数（spec §4.2） */
export const INITIAL_UNLOCKED = 3;

export function emptySave(): Save {
  return {
    version: 2,
    themes: {},
    daily: { results: {}, streak: 0, bestStreak: 0, lastDate: null },
    settings: { haptics: true, gyro: true },
    stats: { puzzlesCompleted: 0, totalPlayMs: 0 },
    lastThemeId: null,
    onboarding: { step: 0 },
    gyro: { asked: false, granted: null },
  };
}

export function emptyPlace(unlocked: boolean): PlaceSave {
  return { unlocked, completed: false, catFound: false, best: {} };
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const isStars = (v: unknown) => v === undefined || (Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 3);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

const isSide = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 2 && (v as number) <= MAX_SIDE;

function validBoard(b: unknown): b is Board {
  if (!isObj(b) || !isSide(b.n) || !Array.isArray(b.pos)) return false;
  if (b.rows !== undefined && !isSide(b.rows)) return false;
  const n = b.n as number;
  const rows = (b.rows as number | undefined) ?? n;
  const N = n * rows;
  if (b.pos.length !== N) return false;
  const seen = new Set<number>();
  for (const c of b.pos) {
    if (!isObj(c) || !Number.isInteger(c.row) || !Number.isInteger(c.col)) return false;
    const r = c.row as number;
    const k = c.col as number;
    if (r < 0 || k < 0 || r >= rows || k >= n) return false;
    seen.add(r * n + k);
  }
  if (b.rot !== undefined && !(Array.isArray(b.rot) && b.rot.length === N && b.rot.every((x) => Number.isInteger(x) && x >= 0 && x <= 3))) return false;
  if (b.down !== undefined && !(Array.isArray(b.down) && b.down.length === N && b.down.every(isBool))) return false;
  return seen.size === N;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const isCount = (v: unknown) => v === undefined || (Number.isInteger(v) && (v as number) >= 0);

function validDial(d: unknown): boolean {
  if (!isObj(d) || (d.solved !== undefined && !isBool(d.solved)) || !isCount(d.wrong) || !isCount(d.hints)) return false;
  return d.slots === undefined || (Array.isArray(d.slots) && d.slots.length <= 8 && d.slots.every((x) => x === null || typeof x === 'string'));
}

function validPlace(p: unknown): boolean {
  if (!isObj(p) || !isBool(p.unlocked) || !isBool(p.completed) || !isBool(p.catFound)) return false;
  if (p.runeFound !== undefined && !isBool(p.runeFound)) return false;
  if (!isObj(p.best) || !isStars(p.stars)) return false;
  for (const [k, b] of Object.entries(p.best)) {
    if (!/^\d+(x\d+)?$/.test(k) || !isObj(b) || !isNum(b.bestTimeMs) || !isNum(b.bestMoves)) return false;
  }
  return true;
}

/** v1 存档（未发布，无真实用户）：v2 spec §5 规定直接丢弃、建新档，不算损坏 */
export function isV1Save(raw: unknown): boolean {
  return isObj(raw) && raw.version === 1;
}

/** 结构校验（不信任磁盘上的内容）；返回 null = 损坏 */
export function parseSave(raw: unknown): Save | null {
  if (!isObj(raw) || raw.version !== 2 || !isObj(raw.themes) || !isObj(raw.onboarding) || !isObj(raw.gyro)) return null;
  if (!isNum(raw.onboarding.step)) return null;
  const g = raw.gyro;
  if (!isBool(g.asked) || !(g.granted === null || isBool(g.granted))) return null;
  for (const t of Object.values(raw.themes)) {
    if (!isObj(t) || !isObj(t.places) || !isBool(t.endingSeen)) return null;
    for (const p of Object.values(t.places)) if (!validPlace(p)) return null;
    if (t.dials !== undefined && !(isObj(t.dials) && Object.entries(t.dials).every(([k, d]) => /^\d+$/.test(k) && validDial(d)))) return null;
  }
  const d = raw.daily;
  if (!isObj(d) || !isObj(d.results) || !isNum(d.streak) || !isNum(d.bestStreak)) return null;
  if (!(d.lastDate === null || (typeof d.lastDate === 'string' && DATE_RE.test(d.lastDate)))) return null;
  for (const [date, r] of Object.entries(d.results)) {
    if (!DATE_RE.test(date) || !isObj(r) || !isNum(r.bestTimeMs) || !isNum(r.bestMoves) || typeof r.placeId !== 'string' || typeof r.themeId !== 'string' || !isStars(r.stars)) return null;
  }
  const st = raw.settings;
  if (!isObj(st) || !isBool(st.haptics) || !isBool(st.gyro)) return null;
  const stats = raw.stats;
  if (!isObj(stats) || !isNum(stats.puzzlesCompleted) || !isNum(stats.totalPlayMs)) return null;
  if (raw.intros !== undefined) {
    const it = raw.intros;
    if (!isObj(it) || (it.rotate !== undefined && !isBool(it.rotate)) || (it.flip !== undefined && !isBool(it.flip))) return null;
  }
  if (!(raw.lastThemeId === null || typeof raw.lastThemeId === 'string')) return null;
  if (raw.inProgress !== undefined) {
    const ip = raw.inProgress;
    if (!isObj(ip) || (ip.mode !== 'journey' && ip.mode !== 'daily') || typeof ip.themeId !== 'string' || typeof ip.placeId !== 'string') return null;
    if (!isNum(ip.seed) || !isNum(ip.moves) || !isNum(ip.elapsedMs)) return null;
    if (ip.date !== undefined && (typeof ip.date !== 'string' || !DATE_RE.test(ip.date))) return null;
    if (!validBoard(ip.board) || ip.n !== ip.board.n) return null;
    if (ip.rows !== undefined && ip.rows !== (ip.board.rows ?? ip.board.n)) return null;
    if (ip.par !== undefined && !(isObj(ip.par) && isNum(ip.par.swaps) && isNum(ip.par.rotations))) return null;
  }
  return raw as unknown as Save;
}

/**
 * 让存档与全部城市的 manifest 对齐：每城新地点补空记录，按 unlockOrder 保证"开局 3 个 + 每首通 1 个"，
 * 指向已不存在地点的 inProgress / lastThemeId 丢弃。幂等。
 */
export function reconcile(save: Save, manifests: Manifest[]): Save {
  for (const m of manifests) {
    const t = (save.themes[m.theme.id] ??= { places: {}, endingSeen: false });
    const order = [...m.places].sort((a, b) => a.unlockOrder - b.unlockOrder);
    for (const p of order) t.places[p.id] ??= emptyPlace(false);
    const completed = order.filter((p) => t.places[p.id].completed).length;
    const shouldOpen = Math.min(order.length, INITIAL_UNLOCKED + completed, gateCap(save, m));
    order.slice(0, shouldOpen).forEach((p) => (t.places[p.id].unlocked = true));
    for (const p of order) if (t.places[p.id].completed) t.places[p.id].unlocked = true;
  }
  const ip = save.inProgress;
  if (ip && !save.themes[ip.themeId]?.places[ip.placeId]) delete save.inProgress;
  else if (ip && !sameGrid(inProgressGrid(ip), expectedGrid(ip, manifests))) delete save.inProgress; // 关卡表改过格数：旧的半局作废
  if (save.lastThemeId && !save.themes[save.lastThemeId]) save.lastThemeId = null;
  return save;
}

/**
 * 星盘锁的章节门槛：第 i 章（i ≥ 1）的第一张牌要等第 i-1 章的星盘解开才开放。
 * 返回最多能开放到第几个 unlockOrder（不含）；没有星盘的主题不设限。已经开放的地点不会被收回（reconcile 只开不关）。
 */
export function gateCap(save: Save, m: Manifest): number {
  const chapters = m.theme.chapters ?? [];
  if (!puzzlesOf(m.theme.id)) return Infinity;
  for (let i = 1; i < chapters.length; i++) {
    if (dialOf(m.theme.id, i - 1) && !dialSolved(save, m.theme.id, i - 1)) return chapters[i].from;
  }
  return Infinity;
}

export const dialSolved = (save: Save, themeId: string, chapter: number): boolean => !!save.themes[themeId]?.dials?.[String(chapter)]?.solved;

/** 这一章的星盘存档（没有就建一个空的） */
export function dialSave(save: Save, themeId: string, chapter: number): DialSave {
  const t = save.themes[themeId];
  t.dials ??= {};
  return (t.dials[String(chapter)] ??= {});
}

/** 主题的全部星盘都解开了（没有星盘 = true） */
export function allDialsSolved(save: Save, themeId: string): boolean {
  const pz = puzzlesOf(themeId);
  return !pz || pz.dials.every((d) => dialSolved(save, themeId, d.chapter));
}

/** 存档里这一局的格（rows 缺省 = 方形） */
export const inProgressGrid = (ip: InProgress): Grid => ({ cols: ip.n, rows: ip.rows ?? ip.n });

/** 这一局应有的格数：旅程看关卡表，每日挑战看星期（抽到竖版图换成竖版格） */
function expectedGrid(ip: InProgress, manifests: Manifest[]): Grid {
  const m = manifests.find((x) => x.theme.id === ip.themeId);
  if (ip.mode === 'daily') return ip.date && m ? dailyGrid(dailySize(ip.date), imageAspect(m)) : inProgressGrid(ip);
  // 城市下标 = theme.order（0,1,2…），不能用传入列表里的位置：recordCompletion 只传当前这一座城
  const place = m?.places.find((p) => p.id === ip.placeId);
  return m && place ? levelGrid(m.theme.id, m.theme.order, place.unlockOrder) : inProgressGrid(ip);
}

export type CompletionResult = {
  firstClear: boolean;
  newBestTime: boolean;
  newBestMoves: boolean;
  /** 本次星级刷新了该地点的最好星级 */
  newBestStars: boolean;
  /** 本次首次通关新解锁的地点 */
  unlockedId: string | null;
};

/** 通关记账（spec §4.2）：最佳成绩各自取最好；首次通关按 unlockOrder 解锁下一个 */
export function recordCompletion(save: Save, manifest: Manifest, placeId: string, grid: Grid | number, timeMs: number, moves: number, stars?: number): CompletionResult {
  const t = save.themes[manifest.theme.id];
  const p = t.places[placeId];
  const firstClear = !p.completed;
  p.completed = true;
  p.unlocked = true;
  const key = typeof grid === 'number' ? String(grid) : gridKey(grid);
  const prev = p.best[key];
  const newBestTime = !prev || timeMs < prev.bestTimeMs;
  const newBestMoves = !prev || moves < prev.bestMoves;
  p.best[key] = {
    bestTimeMs: newBestTime ? Math.round(timeMs) : prev!.bestTimeMs,
    bestMoves: newBestMoves ? moves : prev!.bestMoves,
  };
  const newBestStars = stars !== undefined && stars > (p.stars ?? 0);
  if (newBestStars) p.stars = stars;
  const ip = save.inProgress;
  if (ip && ip.mode === 'journey' && ip.themeId === manifest.theme.id && ip.placeId === placeId) delete save.inProgress;
  addStats(save, timeMs);
  let unlockedId: string | null = null;
  if (firstClear) {
    const before = new Set(Object.entries(t.places).filter(([, s]) => s.unlocked).map(([id]) => id));
    reconcile(save, [manifest]);
    unlockedId = Object.entries(t.places).find(([id, s]) => s.unlocked && !before.has(id))?.[0] ?? null;
  }
  return { firstClear, newBestTime, newBestMoves, newBestStars, unlockedId };
}

/** 完成数在通关时 +1；总用时不在这里加（App 按实际计时累加，含放弃 / 重开 / 未完成的局） */
function addStats(save: Save, _timeMs: number): void {
  save.stats.puzzlesCompleted += 1;
}

// ── 城市解锁（v2 spec §4）：第一城开局开放；前一城完成 ≥4 个地点 → 开放下一城。
//    alwaysOpen 的主题（塔罗）不在这条链上：开局就开放，也不挡后面的城市 ──

export const CITY_UNLOCK_COUNT = 4;

export function completedCount(save: Save, themeId: string): number {
  return Object.values(save.themes[themeId]?.places ?? {}).filter((p) => p.completed).length;
}

export type ThemeGate = { id: string; alwaysOpen?: boolean };

/**
 * themes 按 order 排好（只传 id 时都在链上）；返回每个主题是否开放、还差几个（开放的为 0），
 * 以及挡着它的前一城（prev，开放的或 alwaysOpen 的为 null）。
 */
export function cityLocks(save: Save, themes: (string | ThemeGate)[]): { open: boolean; need: number; prev: string | null }[] {
  let prevChained: string | null = null;
  return themes.map((t) => {
    const g = typeof t === 'string' ? { id: t } : t;
    if (g.alwaysOpen) return { open: true, need: 0, prev: null };
    const before = prevChained;
    prevChained = g.id;
    if (before === null) return { open: true, need: 0, prev: null };
    const need = Math.max(0, CITY_UNLOCK_COUNT - completedCount(save, before));
    return { open: need === 0, need, prev: need === 0 ? null : before };
  });
}

// ── 每日挑战（v2 spec §4）──

/** 'YYYY-MM-DD' 的前一天 / 后一天（按日历日，不受时区与夏令时影响） */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

/** 当前连续天数：最后一次完成是今天或昨天才算数，断了显示 0 */
export function currentStreak(save: Save, today: string): number {
  const last = save.daily.lastDate;
  return last === today || last === addDays(today, -1) ? save.daily.streak : 0;
}

export type DailyRecord = { first: boolean; streak: number };

/** 每日挑战通关：当天第一次记录成绩并更新连续天数；重玩只计入统计 */
export function recordDaily(save: Save, date: string, themeId: string, placeId: string, grid: Grid | number, timeMs: number, moves: number, stars?: number): DailyRecord {
  const g = typeof grid === 'number' ? { cols: grid, rows: grid } : grid;
  const n = g.cols;
  const d = save.daily;
  const ip = save.inProgress;
  if (ip && ip.mode === 'daily') delete save.inProgress;
  addStats(save, timeMs);
  if (d.results[date]) return { first: false, streak: currentStreak(save, date) };
  d.results[date] = { bestTimeMs: Math.round(timeMs), bestMoves: moves, themeId, placeId, n, ...(g.rows !== g.cols ? { rows: g.rows } : {}), ...(stars !== undefined ? { stars } : {}) };
  d.streak = d.lastDate === addDays(date, -1) ? d.streak + 1 : d.lastDate === date ? d.streak : 1;
  d.bestStreak = Math.max(d.bestStreak, d.streak);
  d.lastDate = date;
  return { first: true, streak: d.streak };
}

/** 旅程全部地点的最好星级之和（统计页：x / 地点数 × 3） */
export function totalStars(save: Save): number {
  let sum = 0;
  for (const t of Object.values(save.themes)) for (const p of Object.values(t.places)) sum += p.stars ?? 0;
  return sum;
}

export type LoadResult = { save: Save; notice: string | null };

/** 存档读写。损坏或版本不符 → 原文备份到 save.bak，重置，并给出一次提示；v1 存档直接丢弃（不提示） */
export class SaveStore {
  save: Save = emptySave();
  private writing: Promise<void> = Promise.resolve();
  private manifests: Manifest[] = [];

  constructor(readonly backend: KeyValueStore) {}

  async load(manifests: Manifest[]): Promise<LoadResult> {
    this.manifests = manifests;
    const text = await this.backend.get(SAVE_KEY);
    let notice: string | null = null;
    let save: Save | null = null;
    if (text !== null) {
      let raw: unknown = null;
      try {
        raw = JSON.parse(text);
      } catch {
        raw = null;
      }
      save = parseSave(raw);
      if (!save && !isV1Save(raw)) {
        await this.backend.set(BACKUP_KEY, text);
        notice = '存档无法读取，已备份并重新开始。';
      }
    }
    this.save = reconcile(save ?? emptySave(), manifests);
    await this.flush();
    return { save: this.save, notice };
  }

  /** 设置里的"重置存档"：回到全新状态（设置项保留为默认） */
  async reset(): Promise<void> {
    this.save = reconcile(emptySave(), this.manifests);
    await this.flush();
  }

  /** 串行写入：保证最后一次写入的是最新状态 */
  flush(): Promise<void> {
    const text = JSON.stringify(this.save);
    this.writing = this.writing.then(() => this.backend.set(SAVE_KEY, text));
    return this.writing;
  }
}
